import type { ReactNode } from "react";
import { Code } from "@connectrpc/connect";
import { useQuery } from "@connectrpc/connect-query";
import { skipToken } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";

import { serverNow } from "@yatayat/core/clock";
import { formatDate, formatNepalTime, nepalIsoDate } from "@yatayat/core/date";
import { formatNumber, formatPaisa } from "@yatayat/core/money";
import {
  RegistryService,
  type PermitCheck,
  type ValidityCheck,
  type VerifyVehicleResponse,
} from "@yatayat/gen/yatayat/registry/v1/registry_pb";

import { VehicleBanner, VehicleOutcomeBanner } from "~/components/VehicleOutcomeBanner";
import { VehicleStickerForm, normalizeStickerCode, stickerCodePattern } from "~/components/VehicleStickerForm";
import { useI18n } from "~/lib/i18n";
import { placeName, vehicleClassLabels } from "~/lib/labels";
import { errorCode, retryTransient } from "~/lib/rpc/errors";
import { ui } from "~/lib/ui";

// The path printed on every vehicle's QR sticker; keep it short and stable.
export const Route = createFileRoute("/v/$code")({
  component: VerifyVehicle,
});

function VerifyVehicle() {
  const { code: param } = Route.useParams();
  const { t } = useI18n();
  const code = normalizeStickerCode(param);
  const wellFormed = stickerCodePattern.test(code);
  const check = useQuery(RegistryService.method.verifyVehicle, wellFormed ? { stickerCode: code } : skipToken, {
    retry: retryTransient,
  });

  let body: ReactNode;
  if (check.data) {
    body = <VehicleReport check={check.data} code={code} />;
  } else if (!wellFormed || (check.isError && unknownSticker(check.error))) {
    body = (
      <>
        <VehicleBanner tone="attention" title={t("stickerNotFound")} body={t("stickerNotFoundBody", { code })} />
        <VehicleStickerForm key={code} initialCode={code} />
      </>
    );
  } else if (check.isError) {
    body = (
      <div className="space-y-3">
        <p role="alert" className="text-brand dark:text-brand-dark">
          {t("somethingWrong")}
        </p>
        <button
          type="button"
          onClick={() => void check.refetch()}
          disabled={check.isFetching}
          className="rounded-md border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100 disabled:opacity-60 dark:border-slate-700 dark:hover:bg-slate-900"
        >
          {t("tryAgain")}
        </button>
      </div>
    );
  } else {
    body = (
      <p role="status" className="rounded-2xl bg-slate-100 p-5 text-slate-600 dark:bg-slate-900 dark:text-slate-400">
        {t("verifyChecking")}
      </p>
    );
  }

  return <div className="mx-auto max-w-xl space-y-4">{body}</div>;
}

function unknownSticker(err: unknown): boolean {
  const code = errorCode(err);
  return code === Code.NotFound || code === Code.InvalidArgument;
}

// Matches the api's window for flagging a vehicle for attention.
const attentionDays = 30;
const dayLength = 24 * 60 * 60 * 1000;

function VehicleReport({ check, code }: { check: VerifyVehicleResponse; code: string }) {
  const { t, lang } = useI18n();
  const checkedAt = Number(check.checkedAtMs);
  // Expiry is judged on the day the api checked, as the outcome is.
  const today = dayMs(nepalIsoDate(checkedAt > 0 ? checkedAt : serverNow())) ?? 0;
  const stickerCode = check.stickerCode || code;
  const operator = check.operatorName?.[lang];

  return (
    <article className="space-y-4">
      <VehicleOutcomeBanner outcome={check.outcome} />

      <section className="rounded-xl border border-slate-200 p-4 dark:border-slate-800">
        <p className="text-sm text-slate-600 dark:text-slate-400">{t("plateNo")}</p>
        <p className="mt-0.5 break-all font-mono text-2xl font-bold tracking-wide text-ink sm:text-3xl dark:text-ink-dark">
          {check.plateNo}
        </p>
        {operator && <p className="mt-2 font-medium">{operator}</p>}
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {t(vehicleClassLabels[check.vehicleClass])}
          {check.electric && ` · ${t("electric")}`} · {t("stickerNo", { code: stickerCode })}
        </p>
      </section>

      <dl className="divide-y divide-slate-100 rounded-xl border border-slate-200 text-sm dark:divide-slate-800 dark:border-slate-800">
        <DocumentRow label={t("insurance")} doc={check.insurance} today={today} />
        <DocumentRow label={t("fitness")} doc={check.fitness} today={today} />
      </dl>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">{t("routePermits")}</h2>
        {check.permits.length === 0 ? (
          <p className="text-sm text-slate-600 dark:text-slate-400">{t("noPermits")}</p>
        ) : (
          <ul className="space-y-3">
            {check.permits.map((p) => (
              <PermitItem key={p.permitNo} permit={p} today={today} />
            ))}
          </ul>
        )}
        {check.permits.some((p) => p.officialFare) && (
          <Link to="/fare-check" className="inline-block text-sm text-ink underline-offset-2 hover:underline dark:text-ink-dark">
            {t("otherStretchFare")}
            <span aria-hidden="true"> →</span>
          </Link>
        )}
      </section>

      <Link
        to="/complaints/new"
        search={{ sticker: stickerCode }}
        className="block w-full rounded-lg bg-ink px-4 py-3 text-center font-semibold text-white dark:bg-ink-dark dark:text-slate-950"
      >
        {t("reportThisVehicle")}
      </Link>

      {checkedAt > 0 && (
        <p className="text-center text-xs text-slate-500 dark:text-slate-400">
          {t("checkedAt", { date: formatDate(checkedAt, lang), time: formatNepalTime(checkedAt, lang) })}
        </p>
      )}
      <p className="text-center">
        <Link to="/v" className="text-sm text-ink underline-offset-2 hover:underline dark:text-ink-dark">
          {t("verifyAnother")}
        </Link>
      </p>
    </article>
  );
}

function DocumentRow({ label, doc, today }: { label: string; doc: ValidityCheck | undefined; today: number }) {
  return (
    <div className="flex items-start justify-between gap-4 px-4 py-3">
      <dt className="text-slate-600 dark:text-slate-400">{label}</dt>
      <dd className="text-right font-medium">{doc && <Expiry valid={doc.valid} endsOn={doc.expiresOn} today={today} />}</dd>
    </div>
  );
}

function PermitItem({ permit, today }: { permit: PermitCheck; today: number }) {
  const { t, lang } = useI18n();
  const from = dayMs(permit.validFrom);
  const to = dayMs(permit.validTo);
  const fare = permit.officialFare;

  let state: ReactNode;
  if (permit.valid) {
    state = <Expiry valid endsOn={permit.validTo} today={today} />;
  } else if (to !== null && to < today) {
    state = <Status tone="bad">{t("validExpiredOn", { date: formatDate(to, lang) })}</Status>;
  } else if (from !== null && from > today) {
    state = <Status tone="warn">{t("permitStarts", { date: formatDate(from, lang) })}</Status>;
  } else {
    state = <Status tone="bad">{t("permitNotInForce")}</Status>;
  }

  return (
    <li className="rounded-xl border border-slate-200 p-4 dark:border-slate-800">
      <h3 className="font-semibold">{permit.routeName?.[lang] || permit.routeCode}</h3>
      <p className="text-xs text-slate-500 dark:text-slate-400">{t("permitNo", { no: permit.permitNo })}</p>
      <div className="mt-1 text-sm font-medium">{state}</div>
      {fare && (
        <div className="mt-3 rounded-lg bg-ink/5 p-3 dark:bg-ink-dark/10">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {permit.origin && permit.destination
              ? t("permitFare", { from: placeName(permit.origin, lang), to: placeName(permit.destination, lang) })
              : t("officialFare")}
          </p>
          <p className={ui.h1}>{formatPaisa(fare.paisa, lang)}</p>
          {permit.fareVersionLabel && (
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              {t("fareTableLabel", { label: permit.fareVersionLabel })}
            </p>
          )}
        </div>
      )}
    </li>
  );
}

/** Whether a document is current, warning when it lapses within the attention window. */
function Expiry({ valid, endsOn, today }: { valid: boolean; endsOn: string; today: number }) {
  const { t, lang } = useI18n();
  const end = dayMs(endsOn);
  if (end === null) return null;

  const date = formatDate(end, lang);
  if (!valid) return <Status tone="bad">{t("validExpiredOn", { date })}</Status>;

  const days = Math.round((end - today) / dayLength);
  if (days > attentionDays) return <Status tone="good">{t("validUntil", { date })}</Status>;

  return (
    <>
      <Status tone="warn">
        {days <= 0 ? t("validExpiresToday") : days === 1 ? t("validExpiresTomorrow") : t("validExpiresInDays", { n: formatNumber(days, lang) })}
      </Status>
      <span className="block text-xs font-normal text-slate-600 dark:text-slate-400">{t("validUntil", { date })}</span>
    </>
  );
}

const statusTones = {
  good: { className: "text-emerald-700 dark:text-emerald-400", icon: "✓" },
  warn: { className: "text-amber-700 dark:text-amber-300", icon: "!" },
  bad: { className: "text-red-700 dark:text-red-400", icon: "✕" },
};

function Status({ tone, children }: { tone: keyof typeof statusTones; children: ReactNode }) {
  const look = statusTones[tone];

  return (
    <span className={`block ${look.className}`}>
      <span aria-hidden="true">{look.icon} </span>
      {children}
    </span>
  );
}

/** Midnight UTC of a "YYYY-MM-DD" date, or null when it isn't one; formatDate shows it as that day. */
function dayMs(iso: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}
